import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Box, Typography, CircularProgress } from "@mui/material";
import { formatCurrency } from "@/utils/formatCurrency.ts";
import { calculateSbsiReq } from "@/api/projectsApi";
import { ProjectFormMode } from "@/constants";
import useIncentiveImpactedFields from "@/hooks/useIncentiveImpactedFields";
import IncentiveImpactWarning from "@/components/IncentiveImpactWarning";
import { useAuthStore } from "@/store/authStore.ts";

const MAX_INCENTIVE = 10000;

type Props = {
  mode?: ProjectFormMode;
};

const IncentiveCalculationStep = ({ mode = "create" }: Props) => {
  const { watch, setValue } = useFormContext();
  const [calculatedIncentive, setCalculatedIncentive] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isMarket, setIsMarket] = useState(true);

  // SDCP-1305: on an existing project the incentive is whatever was approved and stored.
  // Nothing on this screen may recalculate or overwrite it — a customer's NEM/SBP status can
  // have flipped since approval, and 311 already-paid projects were being silently restated
  // downwards by exactly this step running on the way to the Save button.
  const isLocked = mode === "edit";

  const { hasIncentiveImpact } = useIncentiveImpactedFields(isLocked);

  // Incentive warnings are administrator-only (client request, 2026-08-18).
  const role = useAuthStore((state) => state.role);
  const canSeeIncentiveWarnings = role === "administrator";

  const accountHash = watch("accountHash");
  const totalProjectCost = Number(watch("totalProjectCost")) || 0;

  const batteryQuantity = Number(watch("batteryQuantity")) || 0;
  const usableCapacityIncentive = Number(watch("usableCapacityIncentive")) || 0;
  const usableCapacity = Number(watch("usableCapacity")) || 0;
  const externalIncentives = Number(watch("externalIncentives")) || 0;
  const storedIncentive = Number(watch("upfrontIncentivePayment")) || 0;

  const capacityPerBattery = usableCapacityIncentive || usableCapacity;
  const totalCapacity = batteryQuantity * capacityPerBattery;

  const displayedIncentive = isLocked
    ? storedIncentive
    : Math.min(calculatedIncentive, MAX_INCENTIVE);
  const isMaxIncentiveReached = !isLocked && calculatedIncentive > MAX_INCENTIVE;

  useEffect(() => {
    if (isLocked) return;

    const calculateSBSI = async () => {
      if (accountHash && totalProjectCost > 0 && totalCapacity > 0) {
        setIsCalculating(true);
        try {
          const response = await calculateSbsiReq({
            accountNumber: accountHash,
            estimated_kwh: totalCapacity,
            total_cost: totalProjectCost,
            extra_incentives: externalIncentives,
          });
          const sbsiAmount = Number(response?.data?.sbsi) || 0;
          const marketStatus = response?.data?.isMarket ?? true;
          setCalculatedIncentive(sbsiAmount);
          setIsMarket(marketStatus);
          setValue("upfrontIncentivePayment", sbsiAmount);
        } catch (error) {
          console.error("Error calculating SBSI:", error);
          setCalculatedIncentive(0);
          setIsMarket(true);
        } finally {
          setIsCalculating(false);
        }
      } else {
        setCalculatedIncentive(0);
        setIsMarket(true);
      }
    };

    calculateSBSI();
  }, [
    isLocked,
    accountHash,
    totalProjectCost,
    externalIncentives,
    totalCapacity,
    setValue,
  ]);

  return (
    <>
      <IncentiveImpactWarning
        show={canSeeIncentiveWarnings && hasIncentiveImpact}
      />

      <Box
        data-testid="incentive-widget"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "200px",
          textAlign: "center",
          backgroundColor: isMaxIncentiveReached ? "#FFFAF1" : "#F4FEFF",
          borderRadius: "8px",
          padding: "32px",
        }}
      >
        <Typography
          sx={{
            padding: "0 0 16px 0",
            display: "flex",
            justifyContent: "center",
            fontWeight: "bold",
            fontSize: "18px",
          }}
          variant={"bodyRegular"}
        >
          {isMaxIncentiveReached
            ? "Upfront Incentive Payment Results"
            : "Upfront Incentive Amount"}
        </Typography>

        {isCalculating ? (
          <CircularProgress data-testid="incentive-loading" />
        ) : (
          <>
            <Typography
              data-testid="incentive-amount"
              sx={{
                fontSize: "48px",
                fontWeight: "bold",
                color: isMaxIncentiveReached ? "#FF9800" : "#4CAF50",
                marginBottom: "16px",
                lineHeight: 1,
              }}
            >
              {formatCurrency(displayedIncentive)}
            </Typography>

            {!isLocked && (
              <Box
                data-testid="incentive-rate-badge"
                sx={{
                  backgroundColor: "#C8E6C9",
                  color: "#2E7D32",
                  padding: "4px 8px",
                  borderRadius: "25px",
                  fontSize: "11px",
                  fontWeight: 600,
                  display: "inline-block",
                  marginTop: "0px",
                  border: "none",
                }}
              >
                {isMarket ? "Market Rate" : "Non-Market Rate"}
              </Box>
            )}

            {isMaxIncentiveReached && (
              <Typography
                data-testid="incentive-cap-message"
                sx={{
                  fontSize: "14px",
                  color: "#373533",
                  marginTop: "16px",
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                Maximum incentive of $10,000 per project reached
              </Typography>
            )}

            {isLocked && (
              <Typography
                data-testid="incentive-locked-message"
                sx={{
                  fontSize: "14px",
                  color: "#373533",
                  marginTop: "16px",
                  textAlign: "center",
                  maxWidth: "420px",
                }}
              >
                This is the incentive currently saved on the project. Updating
                the project will not change it. To recalculate it or set it
                manually, use the Upfront Incentive Payment controls on the
                project details page.
              </Typography>
            )}
          </>
        )}
      </Box>
    </>
  );
};

export default IncentiveCalculationStep;
